import type {
  CommonNetworkProfile,
  LicenceProfileTypesEdit,
  ListResponse,
  ProfileExit,
  ProfileNote,
  ProfileNotes,
  ProfileResponse,
  ProfileTypesEdit,
  RestoreProfilesResponse,
} from '@shared/schemas';
import type { ProfileTypeCategory } from '@shared/schemas/profile-type-blocks';

import api from '@/shared/api/api-client';

import type { TypeCategories } from '../domain/profile-detail.rules';
import type { ProfileDetailGateway } from './profile-detail.gateway';

/**
 * Real implementation over the shared api client: the single place that knows the
 * Profile Detail endpoints and request shapes (kept 1:1 with the original
 * component). `api.get/post` unwrap the `{ success, data }` envelope.
 */
export const httpProfileDetailGateway: ProfileDetailGateway = {
  loadProfile: (id) => api.get<ProfileResponse>(`/profiles/${id}`),

  loadCommonNetwork: (id) => api.get<CommonNetworkProfile[]>(`/profiles/${id}/common-network`),

  enrich: async (id, force) => {
    await api.post(`/profiles/${id}/enrich`, { force });
  },

  deactivate: async (id) => {
    await api.post('/profiles/deactivate', { profile_ids: [id], scope: 'global' });
  },

  reactivate: async (id) => {
    const res = await api.post<RestoreProfilesResponse>('/profiles/restore', { profile_ids: [id] });
    return { immunised: (res.immunised_count ?? 0) > 0 };
  },

  setContactStatus: async (id, contact, verified) => {
    await api.patch(`/profiles/${id}/contact-status`, { contact, verified });
  },

  setExpertValidation: async (id, validated) => {
    await api.post('/profiles/expert-validation', { profile_ids: [id], validated });
  },

  loadAssignableLists: () => api.get<ListResponse[]>('/admin/lists'),

  loadTypeCategories: async () => {
    const rows = await api.get<{ name: string; category: ProfileTypeCategory | null }[]>('/profile-types');
    return Object.fromEntries(rows.map((r) => [r.name, r.category])) as TypeCategories;
  },

  addToList: async (listId, profileId) => {
    await api.post(`/lists/${listId}/profiles`, { profile_ids: [profileId] });
  },

  loadTypes: (id) => api.get<ProfileTypesEdit>(`/profiles/${id}/types`),

  // Type names are free text ("Bank / Institutional Investor"): always encoded.
  removeType: (id, type) => api.delete<ProfileTypesEdit>(`/profiles/${id}/types/${encodeURIComponent(type)}`),

  restoreType: (id, type) =>
    api.post<ProfileTypesEdit>(`/profiles/${id}/types/${encodeURIComponent(type)}/restore`),

  addType: (id, type) => api.post<ProfileTypesEdit>(`/profiles/${id}/types/${encodeURIComponent(type)}`),

  loadLicenceTypes: (id) => api.get<LicenceProfileTypesEdit>(`/profiles/${id}/licence-types`),

  addLicenceType: (id, type) =>
    api.post<LicenceProfileTypesEdit>(`/profiles/${id}/licence-types/${encodeURIComponent(type)}`),

  removeLicenceType: (id, type) =>
    api.delete<LicenceProfileTypesEdit>(`/profiles/${id}/licence-types/${encodeURIComponent(type)}`),

  loadNotes: (id) => api.get<ProfileNotes>(`/profiles/${id}/notes`),

  addNote: (id, content) => api.post<ProfileNote>(`/profiles/${id}/notes`, { content }),

  updateNote: (id, noteId, content) => api.patch<ProfileNote>(`/profiles/${id}/notes/${noteId}`, { content }),

  deleteNote: async (id, noteId) => {
    await api.delete<void>(`/profiles/${id}/notes/${noteId}`);
  },

  createExit: (id, exit) => api.post<ProfileExit>(`/profiles/${id}/exits`, exit),

  updateExit: (id, exitId, exit) => api.patch<ProfileExit>(`/profiles/${id}/exits/${exitId}`, exit),

  deleteExit: async (id, exitId) => {
    await api.delete<void>(`/profiles/${id}/exits/${exitId}`);
  },
};
